import { useEffect, useMemo, useRef } from 'react';

import { Flex, TabPanels, Tabs } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CFTablesV1 from 'screens/Levelup/LevelUpCF/components/CFTablesV1';
import MobileResponsiveColumn from 'screens/Levelup/LevelUpCF/components/MobileResponsiveColumn';
import AddFeedbackButton from 'screens/Levelup/LevelUpCF/components/addFeedbackButton/AddFeedbackButton';
import KebabMenu from 'screens/Levelup/LevelUpCF/gridComponents/KebabMenu';
import { columnToCellMetaMapper } from 'screens/Levelup/LevelUpCF/helpers';
import { useCFTableData, useCFTabsData } from 'screens/Levelup/LevelUpCF/hooks/dataHooks';
import { useSelectedFeedbackViewFromParam } from 'screens/Levelup/hooks/useSelectedFeedbackViewFromParam';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import { sendPageViewEvent, sendViewedFeedbackList } from 'constants/mixpanelHelper';
import { EVENT_SOURCES } from 'constants/trackingEvents';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';

const TABS = {
  PENDING: {
    displayName: 'Pending on me',
    BEKey: 'pendingFeedbackCount',
    mixpanelKey: 'Me-competencies-feedback pending on me',
  },
  RECEIVED: {
    displayName: 'Received by me',
    BEKey: 'receivedFeedbackCount',
    mixpanelKey: 'Me-competencies-feedback received by me',
  },
  SHARED: {
    displayName: 'Shared by me',
    BEKey: 'sharedFeedbackCount',
    mixpanelKey: 'Me-competencies-feedback shared by me',
  },
  REQUESTED: {
    displayName: 'Requested by me',
    BEKey: 'requestedFeedbackCount',
    mixpanelKey: 'Me-competencies-my feedback requests',
  },
};

const CFTabPanelsIntent = () => {
  const tabsArray = useMemo(() => Object.keys(TABS), []);

  const { pushFeedbackView, replaceFeedbackView, selectedFeedbackView } = useSelectedFeedbackViewFromParam();
  const selectedTabIndex = tabsArray.indexOf(selectedFeedbackView);

  const selectedTab = selectedFeedbackView;

  const gridRef = useRef();
  const { isLoading } = useCFTabsData();

  const history = useHistory();

  const employeeId = useSelector(selectLoggedInEmployeeId);

  const { data: cfTableData, isLoading: isCfTableLoading } = useCFTableData({
    feedbackStatus: selectedTab,
  });

  const headerDisplayNameForMobile = {
    PENDING: 'Requested by',
    RECEIVED: 'Received by me',
    SHARED: 'Given to',
    REQUESTED: 'Requested From',
  }[selectedTab];

  const columnDefs = useMemo(() => {
    const dynamicColumns =
      cfTableData?.columns.map(({ id, displayName, uuidType }) =>
        columnToCellMetaMapper({ id, displayName, uuidType, type: selectedTab })
      ) ?? [];

    const kebabMenuColumn = {
      width: 80,
      flex: 1,
      colId: 'kebab-menu',
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params) => <KebabMenu params={params} type={selectedTab} />,
      valueGetter: (params) => params.data,
      field: '',
      headerName: '',
      lockPosition: 'right',
      pinned: 'right',
      suppressHeaderMenuButton: true,
      cellClass: 'locked-col',
    };

    return [...dynamicColumns, kebabMenuColumn];
  }, [cfTableData, selectedTab]);

  const responsiveColumnDef = useMemo(
    () => [
      {
        colId: 'employee',
        field: 'employee',
        id: 'employee',
        sortable: true,
        minWidth: 250,
        // pinned: 'left',
        flex: 1,
        suppressHeaderMenuButton: true,
        headerName: headerDisplayNameForMobile,
        cellRenderer: MobileResponsiveColumn,
        valueGetter: (params) => params?.data,
        filterParams: {
          applyMiniFilterWhileTyping: true,
        },
      },
      {
        width: 80,
        flex: 1,
        colId: 'kebab-menu',
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => <KebabMenu params={params} type={selectedTab} />,
        valueGetter: (params) => params?.data,
        field: '',
        headerName: '',
        lockPosition: 'right',
        suppressHeaderMenuButton: true,
        cellClass: 'locked-col',
      },
    ],
    [cfTableData, selectedTab]
  );

  const handleRequestNewFeedbackClick = () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: employeeId ?? null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      selectedFeedbackView,
    });
  };
  const handleShareNewFeedbackClick = (isSharePublicPraise = false) => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      isSharePublicPraise,
      selectedFeedbackView,
    });
  };

  useEffect(() => {
    if (!selectedFeedbackView) {
      replaceFeedbackView('PENDING');
    }
  }, [replaceFeedbackView, selectedFeedbackView]);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: TABS[selectedFeedbackView]?.mixpanelKey,
      },
    });
  }, [selectedFeedbackView]);

  if (isLoading) {
    return null;
  }

  return (
    <Tabs
      index={selectedTabIndex}
      isLazy
      onChange={(index) => {
        pushFeedbackView(tabsArray[index]);
        sendViewedFeedbackList({
          eventProperties: {
            type: tabsArray[index]?.toProperCase(),
          },
        });
      }}
      p="12px"
      paddingBottom="0px"
      sx={{
        '.chakra-tabs__tab-panels': {
          padding: 0,
          div: {
            paddingBottom: '0px',
          },
        },
      }}
    >
      <Flex>
        <AgGridSearchBar gridRef={gridRef} />
        <AddFeedbackButton
          onClickShareFeedback={(isSharePublicPraise) => handleShareNewFeedbackClick(isSharePublicPraise)}
          onClickRequestFeedback={handleRequestNewFeedbackClick}
        />
      </Flex>

      <TabPanels sx={{ padding: '12px' }}>
        <CFTablesV1
          columnDefs={columnDefs}
          cfTableData={cfTableData}
          selectedTab={selectedTab}
          gridRef={gridRef}
          isCfTableLoading={isCfTableLoading}
          responsiveColumnDef={responsiveColumnDef}
        />
      </TabPanels>
    </Tabs>
  );
};

export { CFTabPanelsIntent };
